import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { InterspireSegment } from '../../types';

interface Props {
  segments: InterspireSegment[];
  value: number | null;
  onChange: (segmentId: number | null) => void;
  disabled?: boolean;
}

function matchesSegment(s: InterspireSegment, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (s.segmentname.toLowerCase().includes(q)) return true;
  if (String(s.segmentid).includes(q)) return true;
  return false;
}

const SegmentSearchPicker: React.FC<Props> = ({ segments, value, onChange, disabled }) => {
  const selected = segments.find((s) => s.segmentid === value) ?? null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () =>
      segments
        .filter((s) => matchesSegment(s, query))
        .slice()
        .sort((a, b) => a.segmentname.localeCompare(b.segmentname)),
    [segments, query]
  );

  useEffect(() => {
    setHighlight(0);
  }, [query, filtered.length]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const pick = (s: InterspireSegment) => {
    onChange(s.segmentid);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[highlight];
      if (target) pick(target);
    }
  };

  if (disabled) {
    return (
      <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
        Interspire unavailable — segment disabled.
      </p>
    );
  }

  return (
    <div ref={rootRef} className="relative mb-3">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between gap-2 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-left text-xs py-1.5 px-2 shadow-sm hover:border-blue-400 dark:hover:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <span
            className={
              selected
                ? 'text-gray-900 dark:text-white truncate'
                : 'text-gray-400 dark:text-slate-500'
            }
          >
            {selected ? selected.segmentname : 'Search segments…'}
          </span>
          <Search className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
        </button>
      ) : (
        <div className="rounded border border-blue-400 dark:border-blue-500 bg-white dark:bg-slate-700 shadow-sm overflow-hidden">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-slate-500 pointer-events-none" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search by name or ID…"
              className="block w-full border-0 bg-transparent text-gray-900 dark:text-white pl-7 pr-7 py-1.5 text-xs focus:ring-0"
              aria-autocomplete="list"
              aria-controls="segment-search-list"
            />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setQuery('');
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
              title="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="px-2 py-0.5 text-[10px] text-gray-500 dark:text-slate-400 border-t border-gray-100 dark:border-slate-600">
            {query.trim()
              ? `${filtered.length} of ${segments.length}`
              : `${segments.length} segments`}
            {selected && (
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                  setQuery('');
                }}
                className="ml-2 underline"
              >
                Clear
              </button>
            )}
          </p>
          <ul
            id="segment-search-list"
            ref={listRef}
            role="listbox"
            className="max-h-40 overflow-y-auto border-t border-gray-100 dark:border-slate-600 divide-y divide-gray-50 dark:divide-slate-700"
          >
            {filtered.map((s, idx) => (
              <li key={s.segmentid} data-idx={idx} role="option" aria-selected={idx === highlight}>
                <button
                  type="button"
                  onClick={() => pick(s)}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`w-full text-left px-2 py-1.5 text-xs flex justify-between gap-2 ${
                    idx === highlight || s.segmentid === value
                      ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-900 dark:text-blue-100'
                      : 'hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-900 dark:text-white'
                  }`}
                >
                  <span className="font-medium truncate">{s.segmentname}</span>
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 flex-shrink-0">
                    #{s.segmentid}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {filtered.length === 0 && (
            <p className="px-2 py-2 text-[11px] text-gray-500 dark:text-slate-400">
              No segments match “{query}”.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SegmentSearchPicker;
